import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowUpRight, Clock } from 'lucide-react';
import SEO from '../components/SEO';
import { Reveal, Eyebrow, MaskedLine } from '../components/Reveal';
import api from '../lib/api';
import { CASE_CATEGORIES, PORTFOLIO_AREAS } from '../data/content';

export default function CaseStudies() {
  const [published, setPublished] = useState(null);
  const [category, setCategory] = useState('All');
  const [query, setQuery] = useState('');

  useEffect(() => {
    api.get('/case-studies').then((r) => setPublished(r.data)).catch(() => setPublished([]));
  }, []);

  const hasPublished = published && published.length > 0;

  const filteredAreas = useMemo(() => {
    return PORTFOLIO_AREAS.filter((a) => {
      const matchCat = category === 'All' || a.category === category;
      const matchQ = !query || a.title.toLowerCase().includes(query.toLowerCase());
      return matchCat && matchQ;
    });
  }, [category, query]);

  const filteredPublished = useMemo(() => {
    if (!hasPublished) return [];
    return published.filter((c) => {
      const matchCat = category === 'All' || c.category === category;
      const matchQ = !query || c.title.toLowerCase().includes(query.toLowerCase());
      return matchCat && matchQ;
    });
  }, [hasPublished, published, category, query]);

  return (
    <>
      <SEO
        title="Case Studies | INSIGHTS STRATEGY — Research in Practice"
        description="Selected areas of consulting, research and capacity-building experience across infrastructure, MSMEs, market research, technology, public policy and sustainability."
        path="/case-studies"
      />
      <section className="relative overflow-hidden bg-brand-deep" data-testid="case-studies-hero">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(23,107,58,0.16),transparent_55%)]" />
        <div className="relative mx-auto max-w-7xl px-5 pb-20 pt-44 lg:px-8 lg:pb-24">
          <Eyebrow dark>Case Studies</Eyebrow>
          <h1 className="mt-6 max-w-4xl font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="case-studies-headline">
            <MaskedLine delay={0.15}>Research in Practice.</MaskedLine>
            <MaskedLine delay={0.3}>Strategy in <span className="text-brand-green">Action.</span></MaskedLine>
          </h1>
          <Reveal delay={0.45}>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-white/60 sm:text-lg">
              Explore selected areas of our consulting, research, and capacity-building experience.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-brand-offwhite">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-24">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative w-full max-w-md">
              <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-body" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search areas of work…"
                aria-label="Search case studies"
                data-testid="case-studies-search"
                className="w-full rounded-full border border-brand-grey bg-white py-3 pl-11 pr-4 text-sm text-brand-black outline-none transition-colors focus:border-brand-green"
              />
            </div>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by category">
              {['All', ...CASE_CATEGORIES].map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  data-testid={`filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  className={`rounded-full border px-4 py-2 text-xs font-semibold transition-all duration-200 ${
                    category === c
                      ? 'border-brand-green bg-brand-green text-white'
                      : 'border-brand-grey bg-white text-brand-black/70 hover:border-brand-green/50 hover:text-brand-black'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {hasPublished ? (
            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {filteredPublished.map((c, i) => (
                <Reveal key={c.case_id} delay={0.05 * i}>
                  <Link to={`/case-studies/${c.slug}`} data-testid={`case-card-${c.slug}`} className="group flex h-full flex-col overflow-hidden rounded-2xl border border-brand-grey bg-white transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_rgba(11,13,11,0.1)]">
                    {c.cover_image && (
                      <div className="h-48 overflow-hidden">
                        <img src={c.cover_image} alt={c.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      </div>
                    )}
                    <div className="flex flex-1 flex-col p-6">
                      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-brand-deepgreen">{c.category}</span>
                      <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-brand-black">{c.title}</h3>
                      <p className="mt-2 text-xs text-brand-body">{[c.sector, c.geography].filter(Boolean).join(' · ')}</p>
                      <span className="mt-5 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-brand-deepgreen">
                        Read Case Study <ArrowUpRight size={14} />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
              {filteredPublished.length === 0 && (
                <p className="col-span-full py-12 text-center text-sm text-brand-body" data-testid="case-studies-empty">No case studies match your search.</p>
              )}
            </div>
          ) : (
            <>
              <Reveal delay={0.05}>
                <div className="mt-10 rounded-2xl border border-brand-green/25 bg-white p-6 sm:p-8" data-testid="case-studies-notice">
                  <div className="flex items-start gap-4">
                    <Clock size={20} className="mt-0.5 shrink-0 text-brand-deepgreen" />
                    <div>
                      <h2 className="font-heading text-lg font-bold text-brand-black">Our areas of work</h2>
                      <p className="mt-2 max-w-3xl text-sm leading-relaxed text-brand-body">
                        The portfolio below describes the areas in which our organisation and parent organisation have built experience. Detailed, client-approved case studies are published progressively — several engagements remain confidential. For engagement-specific detail, please contact us directly.
                      </p>
                    </div>
                  </div>
                </div>
              </Reveal>
              <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {filteredAreas.map((a, i) => (
                  <Reveal key={a.title} delay={0.04 * i}>
                    <div className="group flex h-full flex-col overflow-hidden rounded-2xl border border-brand-grey bg-white transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_rgba(11,13,11,0.08)]" data-testid={`area-card-${i}`}>
                      <div className="relative h-44 overflow-hidden">
                        <img src={a.image} alt={a.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                        <span className="absolute left-4 top-4 rounded-full bg-brand-deep/85 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-brand-green backdrop-blur-sm">{a.category}</span>
                      </div>
                      <div className="flex flex-1 flex-col p-6">
                        <h3 className="font-heading text-base font-bold leading-snug text-brand-black">{a.title}</h3>
                        <p className="mt-2 text-xs text-brand-body">{a.geography}</p>
                        <p className="mt-auto pt-4 text-[11px] font-semibold uppercase tracking-[0.12em] text-brand-body/60">Detailed case study available on request</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
                {filteredAreas.length === 0 && (
                  <p className="col-span-full py-12 text-center text-sm text-brand-body" data-testid="case-studies-empty">No areas match your search.</p>
                )}
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}
